// externals
import { createStateSnapshotTelemetryMiddleware } from '@makemydeal/dr-shared-store';

// types
import type { Middleware } from 'redux';
import type { StateTree } from '@makemydeal/dr-dash-types';

// telemetry readiness / context (shared with selectorDeltaTelemetryMiddleware)
import { telemetryReady, getTelemetryBusinessContext } from './telemetryReadiness';

/**
 * Dash-specific state snapshot telemetry middleware (Rally US2039705, phase 3).
 *
 * Emits per-dispatch snapshots of dr-dash Redux state to New Relic. Snapshots
 * are filtered to the payment-math-relevant allowlist defined in
 * `@makemydeal/dr-shared-store/utils/stateSnapshotFieldsAllowlist`.
 *
 * **Why a dash-specific wrapper?** — Same reason as `correlationMiddleware`:
 * the shared factory has no knowledge of dash business context (dealXgId,
 * offerId). Other apps that adopt this middleware will write their own thin
 * wrappers with their own context extractors (e.g. checkinId for sales-checkin).
 *
 * **Telemetry control.** Server-side feature toggle
 * `enableStateSnapshotTelemetry`. Defaults to **OFF** at bootstrap (like
 * `enableReduxTelemetry`) — state snapshots are
 * significantly larger than correlation events and should only be enabled
 * when actively investigating a payment issue.
 *
 * **Placement.** This middleware MUST run AFTER all reducers have applied
 * their updates. In the middleware chain that means: after `bootstrapMiddleware`,
 * after `offerMiddleware`, after any middleware that dispatches follow-on
 * actions. Placing it early would cause it to capture pre-reducer state and
 * miss same-tick reducer mutations.
 *
 * For implementation details see:
 *   `@makemydeal/dr-shared-store/middleware/stateSnapshotTelemetryMiddleware`
 */
export const stateSnapshotTelemetryMiddleware: Middleware<
    Record<string, never>,
    StateTree
> = createStateSnapshotTelemetryMiddleware<StateTree>({
    // Runtime feature toggle gate. Default OFF at bootstrap — snapshots are
    // expensive; opt in explicitly via LaunchDarkly / app config.
    //
    // Reads the toggle slice DIRECTLY rather than via
    // `featureToggleSelectors.isStateSnapshotTelemetryEnabled(state)` on purpose:
    // that selector runs `featureTogglePreconditionCheck`, which THROWS when
    // called before bootstrap completes. This middleware evaluates `enabled` on
    // every dispatch — including the pre-bootstrap ones — so the selector would
    // throw-and-catch on the hot path for the whole early-session window. The
    // direct read is non-throwing and yields the same default-false result.
    // Kept identical to correlationMiddleware / selectorDeltaTelemetryMiddleware.
    // Type assertion: the toggle lives in AppConfigDataOnlyToggles which
    // StateTree types as Partial<AllFeatureToggles>.
    enabled: (state) => (state.featureToggles as Record<string, boolean | undefined>)?.enableStateSnapshotTelemetry ?? false,

    // Store identifier for cross-store correlation in replay tooling.
    storeId: 'dash-store',

    // Emit a full-state anchor every 100 actions. Deltas alone can be replayed
    // forward from any anchor; without them a missed event breaks the rest of
    // the session.
    anchorCadence: 100,

    // Per-chunk byte cap. New Relic Browser silently drops any PageAction
    // string custom-attribute value above ~4096 chars, so we cap well below
    // to leave headroom for the other attributes in the envelope. Anchors
    // and deltas that exceed this cap are split into multiple rows sharing a
    // `chunkGroupId`; the replay CLI reassembles them by group id. See
    // Rally US2039705 AC-5.
    maxChunkBytes: 3000,

    // Dash-specific business context — echoed on every emitted event so replay
    // tooling can group by deal/offer without joining against another table.
    // Shared with selectorDeltaTelemetryMiddleware so both streams carry
    // identical context. See telemetryReadiness.
    getBusinessContext: getTelemetryBusinessContext,

    // Readiness gate — shared with selectorDeltaTelemetryMiddleware so both
    // tail middlewares flip ready on the SAME dispatch and stay aligned on the
    // shared actionIndex join key. See telemetryReadiness for the rationale.
    waitForReady: telemetryReady
});
